import React, { useState } from 'react';
import emailjs from 'emailjs-com';

// EmailJS configuration
const EMAILJS_PUBLIC_KEY = 'f7x7ZeSu8hK97HFBq';
const EMAILJS_SERVICE_ID = 'service_mn0h9il';
const EMAILJS_TEMPLATE_ID = 'template_zhgn3op';

const ContactModal = ({ isOpen, onClose, defaultSubject = '', defaultTopic = '' }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    topic: defaultTopic,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const clearInput = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
      topic: defaultTopic,
    });
    setErrors({});
    setIsSuccess(false);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^\(\d{3}\)\s\d{3}-\d{4}$/;
    return re.test(phone);
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return value;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleClose = () => {
    clearInput();
    onClose();
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    
    // Enhanced Validation - Check all required fields
    const newErrors = {};
    
    // First Name validation
    if (!formData.firstName || !formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }
    
    // Last Name validation
    if (!formData.lastName || !formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }
    
    // Email validation
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (xxx) xxx-xxxx';
    }
    
    // Topic validation
    if (!formData.topic || !formData.topic.trim() || formData.topic === '') {
      newErrors.topic = 'Please select a topic for your inquiry';
    }
    
    // Message validation
    if (!formData.message || !formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    // If there are validation errors, prevent submission
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error field for better UX
      const firstErrorField = document.querySelector('.text-red-400')?.closest('div')?.querySelector('input, select, textarea');
      if (firstErrorField) {
        firstErrorField.focus();
      }
      return;
    }

    // Clear any previous errors
    setErrors({});
    setIsLoading(true);

    try {
      const templateParams = {
        from_name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        reply_to: formData.email.trim(),
        phone_number: formData.phone,
        subject: formData.topic,
        message: formData.message.trim(),
        to_name: 'TRNF',
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: '',
          topic: defaultTopic,
        });
      }, 2000);

    } catch (error) {
      console.error('Failed to send email:', error);
      setErrors({ submit: 'Failed to send message. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      ></div>
      
      {/* Modal */}
      <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 w-full max-w-md sm:max-w-lg lg:max-w-2xl max-h-[90vh] overflow-y-auto">
        
        {/* Success Message */}
        {isSuccess && (
          <div className="absolute inset-0 bg-green-600/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl flex items-center justify-center z-10">
            <div className="text-center text-white p-6">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Message Sent!</h3>
              <p className="text-sm sm:text-base">Thank you for contacting us. We'll get back to you soon.</p>
            </div>
          </div>
        )}

        <div className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white drop-shadow-lg">
              Contact Us
            </h2>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={sendEmail} className="space-y-4 sm:space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm sm:text-base font-medium text-white mb-2 drop-shadow-md">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent backdrop-blur-sm text-sm sm:text-base min-h-[48px]"
                  placeholder="Your first name"
                />
                {errors.firstName && <p className="text-red-400 text-xs sm:text-sm mt-1 drop-shadow-md">{errors.firstName}</p>}
              </div>
              
              <div>
                <label className="block text-sm sm:text-base font-medium text-white mb-2 drop-shadow-md">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent backdrop-blur-sm text-sm sm:text-base min-h-[48px]"
                  placeholder="Your last name"
                />
                {errors.lastName && <p className="text-red-400 text-xs sm:text-sm mt-1 drop-shadow-md">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm sm:text-base font-medium text-white mb-2 drop-shadow-md">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 sm:px-4 py-3 sm:py-4 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent backdrop-blur-sm text-sm sm:text-base min-h-[48px]"
                placeholder="your.email@example.com"
              />
              {errors.email && <p className="text-red-400 text-xs sm:text-sm mt-1 drop-shadow-md">{errors.email}</p>}
            </div>

            {/* Phone Field */}
            <div>
              <label className="block text-sm sm:text-base font-medium text-white mb-2 drop-shadow-md">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 sm:px-4 py-3 sm:py-4 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent backdrop-blur-sm text-sm sm:text-base min-h-[48px]"
                placeholder="(555) 123-4567"
              />
              {errors.phone && <p className="text-red-400 text-xs sm:text-sm mt-1 drop-shadow-md">{errors.phone}</p>}
            </div>

            {/* Topic Field */}
            <div>
              <label className="block text-sm sm:text-base font-medium text-white mb-2 drop-shadow-md">
                Topic *
              </label>
              <select
                name="topic"
                value={formData.topic}
                onChange={handleInputChange}
                className="w-full px-3 sm:px-4 py-3 sm:py-4 rounded-xl bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent backdrop-blur-sm text-sm sm:text-base min-h-[48px]"
              >
                <option value="" className="bg-gray-800 text-white">Select a topic</option>
                <option value="Program Information" className="bg-gray-800 text-white">Program Information</option>
                <option value="Volunteer Request" className="bg-gray-800 text-white">Volunteer Request</option>
                <option value="General Inquiry" className="bg-gray-800 text-white">General Inquiry</option>
                <option value="Donation" className="bg-gray-800 text-white">Donation</option>
                <option value="Other" className="bg-gray-800 text-white">Other</option>
              </select>
              {errors.topic && <p className="text-red-400 text-xs sm:text-sm mt-1 drop-shadow-md">{errors.topic}</p>}
            </div>

            {/* Message Field */}
            <div>
              <label className="block text-sm sm:text-base font-medium text-white mb-2 drop-shadow-md">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-3 sm:px-4 py-3 sm:py-4 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent backdrop-blur-sm resize-none text-sm sm:text-base min-h-[100px]"
                placeholder="Tell us about your interest in our programs..."
              />
              {errors.message && <p className="text-red-400 text-xs sm:text-sm mt-1 drop-shadow-md">{errors.message}</p>}
            </div>

            {/* Submit Button */}
            <div className="pt-2 sm:pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl text-base sm:text-lg transition-all duration-300 transform hover:scale-105 disabled:transform-none shadow-lg hover:shadow-xl drop-shadow-lg min-h-[48px] flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
              {errors.submit && <p className="text-red-400 text-xs sm:text-sm mt-2 text-center drop-shadow-md">{errors.submit}</p>}
            </div>
          </form>

          {/* Contact Information */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/20">
            <div className="text-center text-white">
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 drop-shadow-lg">Or Contact Us Directly</h3>
              <div className="space-y-1 sm:space-y-2 text-sm sm:text-base drop-shadow-md">
                <p>
                  <span className="font-medium">Phone:</span> (410) 923-6800
                </p>
                <p>
                  <span className="font-medium">Address:</span> 3153 Ady Rd, Street, MD
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal; 